/**
 * CelShapes — the authored shape vocabulary for ability VFX.
 *
 * The look comes from `docs/reference/vfx/*.jpg`: a hot near-white core
 * falling through a saturated body to a DARK rim, in hard cel bands — not a
 * soft gradient. Chunky angular shapes, not wisps. Every builder here encodes
 * that ramp procedurally in TSL instead of sampling the reference textures,
 * for two reasons that beat a texture bind per effect:
 *
 *   1. `colorHex` is a gameplay value — five players must be told apart by the
 *      colour of what is being cast at them. A computed ramp tints cleanly;
 *      a JPG bakes its palette (and its compression noise on black).
 *   2. Hard band edges stay crisp at any scale. A 512² texture stretched over
 *      Robotaxi's 20m radius goes soft exactly where the cel look needs edges.
 *
 * Everything is a node material (WebGPU crashes legacy ones), and every
 * per-instance value (tint, progress, fade) rides a UNIFORM, never a TSL
 * const — a const bakes its value into the shader and every distinct value
 * becomes its own program compile (the ParticleUtils lesson).
 *
 * Builders return `{ mesh, u }` where `u` holds the uniforms the caller
 * animates. Geometry is shared out of a module cache — callers must NOT
 * dispose it (AbilityVFX checks `userData.sharedGeo`).
 */

import * as THREE from 'three/webgpu';
import {
  Fn,
  uv,
  float,
  vec3,
  smoothstep,
  step,
  uniform,
  mix,
  atan,
  fract,
  time,
} from 'three/tsl';

// ─── Shared geometry (never disposed by callers) ────────────────

let _circleGeo = null;
let _planeGeo = null;
let _hexPrismGeo = null;

function circleGeo() {
  if (!_circleGeo) _circleGeo = new THREE.CircleGeometry(1, 64);
  return _circleGeo;
}
function planeGeo() {
  if (!_planeGeo) _planeGeo = new THREE.PlaneGeometry(1, 1);
  return _planeGeo;
}
function hexPrismGeo() {
  if (!_hexPrismGeo) _hexPrismGeo = new THREE.CylinderGeometry(1, 1, 1, 6, 1, true);
  return _hexPrismGeo;
}

// ─── Node helpers ───────────────────────────────────────────────

/** 1 inside [a,b] with a hard-but-antialiased edge. Cel bands are made of these. */
const band = (x, a, b, aa = 0.02) =>
  smoothstep(float(a - aa), float(a + aa), x).mul(smoothstep(float(b + aa), float(b - aa), x));

/** The four-stop ramp every reference sheet uses, from a single tint uniform. */
function rampColors(tint) {
  return {
    core: mix(tint, vec3(1, 1, 1), 0.88), // near-white heart
    hot: mix(tint, vec3(1, 1, 1), 0.45), // bright inner band
    body: tint, // the saturated identity colour
    rim: tint.mul(0.32), // the dark outline that makes it pop
  };
}

function baseMaterial({ additive = false, depthTest = true } = {}) {
  const mat = new THREE.MeshBasicNodeMaterial();
  mat.transparent = true;
  mat.depthWrite = false;
  mat.depthTest = depthTest;
  mat.side = THREE.DoubleSide;
  if (additive) mat.blending = THREE.AdditiveBlending;
  return mat;
}

function flatOnGround(mesh, x, y, z, yaw = 0) {
  mesh.rotation.x = -Math.PI / 2;
  mesh.rotation.z = yaw;
  mesh.position.set(x, y, z);
  mesh.frustumCulled = false;
  return mesh;
}

// ─── Builders ───────────────────────────────────────────────────

/**
 * The hollow burst ring (sphere_burst.jpg): dark outline, saturated band,
 * white-hot band, saturated inner lip — hollow middle so a player standing
 * inside stays readable. Unit mesh; the caller animates `mesh.scale` out to
 * the ability's exact radius. `bandStart` narrows the ring for huge radii so a
 * 20m ability doesn't wear a 7m-thick band.
 */
export function burstRing(colorHex, { bandStart = 0.6 } = {}) {
  const mat = baseMaterial();
  const tint = uniform(new THREE.Color(colorHex));
  const fade = uniform(1);
  const inner = uniform(bandStart);
  const c = rampColors(tint);

  const r = uv().sub(0.5).mul(2).length();
  // Band edges expressed as a 0..1 span between `inner` and the outer edge.
  const q = r.sub(inner).div(float(1).sub(inner));
  mat.colorNode = Fn(() => {
    let col = c.rim;
    col = mix(col, c.body, band(q, 0.1, 0.86, 0.04));
    col = mix(col, c.hot, band(q, 0.28, 0.62, 0.04));
    col = mix(col, c.core, band(q, 0.38, 0.52, 0.04));
    return col;
  })();
  mat.opacityNode = Fn(() => band(q, 0.0, 1.0, 0.03).mul(fade))();

  const mesh = new THREE.Mesh(circleGeo(), mat);
  mesh.userData.sharedGeo = true;
  return { mesh, u: { fade, inner, tint } };
}

/**
 * The melee slash (cone_sweep.jpg): a filled sector with a white core at the
 * apex, radial streaks, a dark rim — and a bright leading edge that WIPES
 * across the sector as `progress` runs 0→1. The sector's range and half-angle
 * are the ability's own numbers; the wipe is the strike beat.
 */
export function slashArc(range, halfAngleDeg, colorHex) {
  const half = (halfAngleDeg * Math.PI) / 180;
  const mat = baseMaterial();
  const tint = uniform(new THREE.Color(colorHex));
  const fade = uniform(1);
  const progress = uniform(0);
  const halfU = uniform(half);
  const c = rampColors(tint);

  const local = uv().sub(0.5).mul(2);
  const r = local.length();
  const ang = atan(local.x, local.y); // 0 = the quad's +Y = caster forward

  mat.colorNode = Fn(() => {
    // Radial ramp: white heart near the apex through the saturated body.
    let col = c.body;
    col = mix(col, c.hot, smoothstep(float(0.72), float(0.3), r));
    col = mix(col, c.core, smoothstep(float(0.45), float(0.12), r));
    // Radial streaks — the chunky spokes in the reference cone.
    const spoke = step(float(0.55), fract(ang.mul(9.0)));
    col = mix(col, c.hot, spoke.mul(0.35));
    // Dark rim at the range edge.
    col = mix(col, c.rim, band(r, 0.9, 1.0, 0.03));
    // The wipe's leading edge burns white.
    const lead = mix(float(-1.15), float(1.15), progress).mul(halfU);
    col = mix(col, c.core, band(ang.sub(lead), -0.32, 0.02, 0.05));
    return col;
  })();

  mat.opacityNode = Fn(() => {
    const inCone = step(ang.abs(), halfU).mul(step(r, float(1.0)));
    // Revealed behind the wipe, with a hard front.
    const lead = mix(float(-1.15), float(1.15), progress).mul(halfU);
    const revealed = smoothstep(lead.add(0.03), lead.sub(0.06), ang);
    return inCone.mul(revealed).mul(fade).mul(0.92);
  })();

  const mesh = new THREE.Mesh(circleGeo(), mat);
  mesh.scale.setScalar(range);
  mesh.userData.sharedGeo = true;
  return { mesh, u: { fade, progress, tint } };
}

/**
 * The impact star (impact_flash.jpg): four long and four short points, white
 * heart, saturated body, dark outline. A Sprite so it always faces the match
 * camera — an impact must read from 55° overhead, where a flat ground star
 * would foreshorten to a line.
 */
export function starFlash(colorHex, { additive = false } = {}) {
  const mat = new THREE.SpriteNodeMaterial();
  mat.transparent = true;
  mat.depthWrite = false;
  if (additive) mat.blending = THREE.AdditiveBlending;
  const tint = uniform(new THREE.Color(colorHex));
  const fade = uniform(1);
  const spin = uniform(Math.random() * Math.PI * 2);
  mat.rotationNode = spin;
  const c = rampColors(tint);

  const local = uv().sub(0.5).mul(2);
  const r = local.length();
  const ang = atan(local.x, local.y);
  // Star radius: long points on the cardinals, short on the diagonals.
  const longP = ang.mul(2).cos().abs().pow(10).mul(0.68);
  const shortP = ang.mul(2).sin().abs().pow(10).mul(0.34);
  const R = float(0.3).add(longP).add(shortP);
  const q = r.div(R);

  mat.colorNode = Fn(() => {
    let col = c.rim;
    col = mix(col, c.body, band(q, 0.0, 0.84, 0.05));
    col = mix(col, c.hot, band(q, 0.0, 0.6, 0.05));
    col = mix(col, c.core, band(q, 0.0, 0.34, 0.05));
    return col;
  })();
  mat.opacityNode = Fn(() => smoothstep(float(1.0), float(0.94), q).mul(fade))();

  const sprite = new THREE.Sprite(mat);
  sprite.frustumCulled = false;
  return { mesh: sprite, u: { fade, spin, tint } };
}

/**
 * The hexagonal self ring (self_aura.jpg) — hexagonal ON PURPOSE, so a buff
 * never reads as a ground zone at a glance. Flat at the feet; the caller
 * scales it. Same four-stop ramp on hex distance instead of radius.
 */
export function hexRing(colorHex) {
  const mat = baseMaterial();
  const tint = uniform(new THREE.Color(colorHex));
  const fade = uniform(1);
  const c = rampColors(tint);

  const local = uv().sub(0.5).mul(2);
  // Pointy-top hex distance.
  const hexD = local.y.abs().max(local.x.abs().mul(0.866).add(local.y.abs().mul(0.5)));

  mat.colorNode = Fn(() => {
    let col = c.rim;
    col = mix(col, c.body, band(hexD, 0.6, 0.9, 0.03));
    col = mix(col, c.core, band(hexD, 0.68, 0.8, 0.03));
    return col;
  })();
  mat.opacityNode = Fn(() => band(hexD, 0.56, 0.97, 0.02).mul(fade))();

  const mesh = new THREE.Mesh(circleGeo(), mat);
  mesh.userData.sharedGeo = true;
  return { mesh, u: { fade, tint } };
}

/**
 * The buff shell: a six-sided open prism around the body (the aura made
 * volumetric), additive, with energy bands scrolling upward and a fresnel-free
 * vertical fade so it reads as a shell, not a tube. Lives for the buff's
 * durationMs — a buff that is live must LOOK live (the StatusAura doctrine).
 */
export function hexShell(colorHex, radius = 1.1, height = 2.6) {
  const mat = baseMaterial({ additive: true });
  const tint = uniform(new THREE.Color(colorHex));
  const fade = uniform(1);
  const c = rampColors(tint);

  const vy = uv().y; // 0 bottom → 1 top
  mat.colorNode = Fn(() => mix(c.body, c.hot, band(fract(vy.mul(3).sub(time.mul(0.9))), 0.0, 0.28, 0.06)))();
  mat.opacityNode = Fn(() => {
    const vertical = smoothstep(float(1.0), float(0.55), vy).mul(smoothstep(float(0.0), float(0.12), vy));
    const bands = float(0.4).add(band(fract(vy.mul(3).sub(time.mul(0.9))), 0.0, 0.28, 0.06).mul(0.6));
    return vertical.mul(bands).mul(fade).mul(0.5);
  })();

  const mesh = new THREE.Mesh(hexPrismGeo(), mat);
  mesh.scale.set(radius, height, radius);
  mesh.frustumCulled = false;
  mesh.userData.sharedGeo = true;
  return { mesh, u: { fade, tint } };
}

/**
 * The line strike (line_beam.jpg): a ground band with a white core line,
 * saturated body, dark edges — and a pulse head that travels 0→1 down the
 * length as `progress` runs. Geometry is (width × length), the ability's own
 * numbers; laid flat and yawed by the caller.
 */
export function lineWall(lengthUnits, width, colorHex) {
  const mat = baseMaterial();
  const tint = uniform(new THREE.Color(colorHex));
  const fade = uniform(1);
  const progress = uniform(0);
  const c = rampColors(tint);

  const along = uv().y;
  const across = uv().x.sub(0.5).abs().mul(2);

  mat.colorNode = Fn(() => {
    let col = c.rim;
    col = mix(col, c.body, band(across, 0.0, 0.82, 0.04));
    col = mix(col, c.hot, band(across, 0.0, 0.5, 0.04));
    col = mix(col, c.core, band(across, 0.0, 0.22, 0.04));
    // The travelling head burns white across the whole width.
    col = mix(col, c.core, band(along.sub(progress), -0.1, 0.01, 0.03));
    return col;
  })();
  mat.opacityNode = Fn(() => {
    const revealed = smoothstep(progress.add(0.015), progress.sub(0.05), along);
    return revealed.mul(smoothstep(float(1.0), float(0.93), across)).mul(fade).mul(0.9);
  })();

  const mesh = new THREE.Mesh(planeGeo(), mat);
  mesh.scale.set(width, lengthUnits, 1);
  mesh.userData.sharedGeo = true;
  return { mesh, u: { fade, progress, tint } };
}

/**
 * The zone duration marker (ground_zone.jpg): a solid outer band, a rotating
 * ring of glyph ticks, a thin inner lip — hollow middle. This is the "still
 * dangerous" indicator for placed areas, drawn at the authored radius for the
 * authored durationMs. Glyphs rotate via `time`, costing zero CPU per frame.
 */
export function zoneRing(colorHex, { radius = 3 } = {}) {
  const mat = baseMaterial();
  const tint = uniform(new THREE.Color(colorHex));
  const fade = uniform(1);
  // Band widths are authored for a ~3m zone. Deindexed is 10m and Starlink
  // 12m — proportional bands there become metre-thick hoops that wall off the
  // whole fight (seen, not guessed: two ults filled the mockup screen).
  // Compress the bands toward the outer edge so WORLD thickness stays put.
  const k = uniform(Math.min(1, Math.max(0.28, 3.2 / radius)));
  const c = rampColors(tint);

  const local = uv().sub(0.5).mul(2);
  // Remap so band coords live in the outer `k` fraction of the radius.
  const r = float(1).sub(float(1).sub(local.length()).div(k));
  const a01 = atan(local.x, local.y).mul(1 / (Math.PI * 2)).add(0.5);
  const ticks = step(float(0.4), fract(a01.mul(20).add(time.mul(0.12))));

  mat.colorNode = Fn(() => {
    let col = c.rim;
    col = mix(col, c.body, band(r, 0.9, 0.985, 0.015)); // solid outer band
    col = mix(col, c.core, band(r, 0.925, 0.95, 0.012)); // white keyline
    col = mix(col, c.body, band(r, 0.7, 0.85, 0.015).mul(ticks)); // glyph ticks
    col = mix(col, c.hot, band(r, 0.6, 0.64, 0.012)); // inner lip
    return col;
  })();
  mat.opacityNode = Fn(() => {
    const outer = band(r, 0.88, 1.0, 0.015);
    const glyphs = band(r, 0.7, 0.85, 0.015).mul(ticks).mul(0.7);
    const lip = band(r, 0.59, 0.65, 0.012).mul(0.6);
    return outer.add(glyphs).add(lip).min(1).mul(fade).mul(0.62);
  })();

  const mesh = new THREE.Mesh(circleGeo(), mat);
  mesh.userData.sharedGeo = true;
  return { mesh, u: { fade, tint } };
}

/**
 * A jagged bolt between two points — Chain Lightning's link. A quad whose
 * white core zigzags in shader space so the arc reads as electricity, not as
 * a ruler. `seed` shifts the zigzag so three simultaneous links differ.
 */
export function boltBeam(colorHex) {
  const mat = baseMaterial({ additive: true });
  const tint = uniform(new THREE.Color(colorHex));
  const fade = uniform(1);
  const seed = uniform(Math.random() * 10);
  const c = rampColors(tint);

  const along = uv().y;
  // Triangle-wave zigzag of the core line across the quad.
  const zig = fract(along.mul(5.5).add(seed)).mul(2).sub(1).abs().mul(2).sub(1).mul(0.42);
  const dist = uv().x.sub(0.5).mul(2).sub(zig).abs();

  mat.colorNode = Fn(() => mix(c.body, c.core, smoothstep(float(0.34), float(0.06), dist)))();
  mat.opacityNode = Fn(() => smoothstep(float(0.62), float(0.18), dist).mul(fade))();

  const mesh = new THREE.Mesh(planeGeo(), mat);
  mesh.userData.sharedGeo = true;
  return { mesh, u: { fade, seed, tint } };
}

export { flatOnGround };
